/**
 * Templates for the compatible spare parts modal results.
 */

// Handle both PascalCase and camelCase JSON property names.
const prop = (obj, name) => {
  if (!obj) return undefined;
  const lower = name.charAt(0).toLowerCase() + name.slice(1);
  const upper = name.charAt(0).toUpperCase() + name.slice(1);
  return obj[upper] !== undefined ? obj[upper] : obj[lower];
};

const escapeAttribute = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const createProductItem = (item) => {
  const image = prop(item, 'ImageUrl');
  const title = prop(item, 'Name') || '';
  const itemNumber = prop(item, 'ItemNo') || '';
  const addToCart = prop(item, 'AddToCart') || {};
  const maxQuantity = prop(addToCart, 'MaxQuantity') || 10000;
  const canAddToCart =
    prop(addToCart, 'CanAddToCart') !== false && prop(addToCart, 'RequiresConfigurator') !== true;

  const orderControl = canAddToCart
    ? `<a href="#" class="product-item__order" data-spareparts="add" title="Add to cart"><i class="eva eva-shopping-cart-outline"></i></a>`
    : '';

  return `
        <div class="product-item${
          canAddToCart ? '' : ' product-item--unavailable'
        }" data-item-number="${escapeAttribute(itemNumber)}">
            <div class="product-item__image">
                ${image ? `<img src="${image}" alt="${title}" />` : ''}
                <div class="product-item__quantity">
                    <div class="number-input">
                        <button class="number-input__button number-input__button--minus" type="button" title="Minus"></button>
                        <input type="text" class="number-input__field" min="1" max="${maxQuantity}" value="1" aria-label="${title}" />
                        <button class="number-input__button number-input__button--plus" type="button" title="Plus"></button>
                    </div>
                </div>
                ${orderControl}
            </div>
            <p class="product-item__headline">${title}</p>
            <p class="product-item__number">${itemNumber}</p>
        </div>`;
};

export const loadingTemplate = () => `<p>Loading&hellip;</p>`;

/**
 * Skeleton loader shown while the compatible spare parts are being fetched.
 * Mirrors the results layout (title + count line + product grid).
 */
export const resultsLoadingTemplate = (count = 8) => {
  const cards = Array.from({ length: count })
    .map(
      () =>
        `<span class="product-compatible-modal__skeleton product-compatible-modal__skeleton--card"></span>`,
    )
    .join('');

  return `
        <p class="product-compatible-modal__results-title">Results</p>
        <span class="product-compatible-modal__skeleton product-compatible-modal__skeleton--line"></span>
        <div class="product-grid">${cards}</div>`;
};

/**
 * Builds the "Detected Hearing aid" card (markup taken from the static frontend
 * `product-card` partial). Returns an empty string when there is nothing to show.
 */
export const createDetectedCard = (data) => {
  if (prop(data, 'Error')) {
    return '';
  }

  const modelInfo = prop(data, 'ModelInfo');
  if (!modelInfo) {
    return '';
  }

  const family = prop(modelInfo, 'Family') || '';
  const wearingStyle = prop(modelInfo, 'WearingStyle') || '';
  const model = prop(modelInfo, 'Model') || '';
  const image = prop(modelInfo, 'ImageUrl');
  const title = [family, wearingStyle].filter(Boolean).join(' ') || model;
  const sku = model;

  return `
        <p class="product-compatible-modal__detected-title">Detected Hearing aid</p>
        <div class="product-card">
            <div class="product-card__content">
                ${image ? `<img src="${image}" alt="${title}" class="product-card__image" />` : ''}
                <div class="product-card__text">
                    <p class="product-card__title">${title}</p>
                    <p class="product-card__subtitle">${sku}</p>
                </div>
            </div>
            <button type="button" class="product-card__remove" data-spareparts="reset" aria-label="Reset">
                <span class="eva eva-trash-2-outline"></span>
            </button>
        </div>`;
};

/**
 * Builds the <option> markup for a dropdown, keeping a leading placeholder.
 * @param {Array<{Value:string, Label:string}>} options
 * @param {string} placeholder
 */
export const createOptions = (options, placeholder) => {
  const placeholderOption = `<option value="" disabled selected hidden>${placeholder}</option>`;
  const items = (options || [])
    .map((o) => `<option value="${prop(o, 'Value')}">${prop(o, 'Label')}</option>`)
    .join('');
  return placeholderOption + items;
};

export const createResultsTemplate = (data) => {
  const error = prop(data, 'Error');
  if (error) {
    return `<p class="product-compatible-modal__error">${error}</p>`;
  }

  const items = prop(data, 'Accessories') || [];
  const paging = prop(data, 'Paging');
  const count = (paging && prop(paging, 'TotalCount')) || items.length;

  if (!items.length) {
    return '';
  }

  const cards = items.map(createProductItem).join('');

  return `
        <p class="product-compatible-modal__results-title">Results</p>
        <p class="product-compatible-modal__results-count"><strong>${count}</strong> compatible spare part(s)</p>
        <div class="product-grid">${cards}</div>`;
};
